/**
 * How a thing on the design board looks (design study 05, 3A; Jason,
 * 2026-10-04: "notes should appear on a sorta scrap of paper rather than just
 * floating text", "adding a link should show the link but also extract the
 * image related to the link, use the same scrap of paper style for the
 * link"): a note is its words on a cut sheet, a link is its page's picture,
 * title and site on the same sheet, and a picture stays a picture. Each scrap
 * lies with a hair of tilt of its own, kept with it.
 */

/** Its tilt in degrees, between −1.5 and 1.5, the same every time for the same thing. */
export function tiltOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.round((((h >>> 0) % 3001) / 1000 - 1.5) * 100) / 100;
}

/** A page's address, as opposed to a picture's: a picture brought from a page sits on a scrap with the page's title. */
export function isPageAddress(url: string | null | undefined): boolean {
  if (!url || !/^https?:\/\//i.test(url)) return false;
  try {
    return !/\.(jpe?g|png|gif|webp|avif|heic|bmp|tiff?)$/i.test(new URL(url).pathname);
  } catch {
    return false;
  }
}

/** The site a page is on, as a scrap names it: "pinterest.com". */
export function siteOf(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** How much taller a scrap stands than its picture: its title and site under it, in board units. */
export const SCRAP_FOOT = 64;

/**
 * Where the board scrolls to open on one thing, a line of the timeline linking to it (P-26: "a link to the thing"): that
 * thing in the middle of the view, never scrolled past the paper's start. `origin` is how far in the board is drawn.
 */
export function scrollOnto(it: { x: number; y: number; w: number; h: number }, view: { w: number; h: number }, zoom: number, origin: number): { left: number; top: number } {
  return {
    left: Math.max(0, Math.round((origin + it.x + it.w / 2) * zoom - view.w / 2)),
    top: Math.max(0, Math.round((origin + it.y + it.h / 2) * zoom - view.h / 2)),
  };
}
